/**
 * User-facing chart assistant messages — never expose raw backend/infra text in the UI.
 */

/**
 * True when a ChartMutationPlan.summary is safe to show analysts.
 * Hides IR/projector jargon such as "Chart mutation plan from Pure LLM IR".
 * @param {unknown} summary
 * @returns {boolean}
 */
export function isAnalystVisibleChartPlanSummary(summary) {
    if (typeof summary !== 'string') return false;
    const text = summary.trim();
    if (!text) return false;
    const lower = text.toLowerCase();
    if (lower.includes('pure llm ir')) return false;
    if (lower.includes('chart mutation plan')) return false;
    return true;
}

/** Backend/infra messages that should never appear in the chart assistant UI. */
export function isInternalChartWarning(warning) {
    if (typeof warning !== 'string' || !warning.trim()) return true;
    const lower = warning.toLowerCase();
    if (/^chart-op\s+#\d+:/i.test(warning.trim())) return true;
    return lower.includes('wikidata')
        || lower.includes('rate-limit')
        || lower.includes('rate limited')
        || lower.includes('throttled')
        || lower.includes('kg v2')
        || lower.includes('kgv2')
        || lower.includes('graph neighbor')
        || lower.includes('grounded in linked product')
        || lower.includes('resolved additional entities from linked report text')
        || lower.includes('could not resolve')
        || lower.includes('skipped.')
        || lower.includes('preview required')
        || lower.includes('auto-applied')
        || lower.includes('merge/dedupe')
        || lower.includes('from-scratch build')
        || lower.includes('no linked report')
        || lower.includes('no entity-cards on this chart')
        || lower.includes('ranking suggestions against');
}

/** Detect raw technical payloads (entity dumps, IDs, internal errors). */
export function isTechnicalChartMessage(text) {
    if (typeof text !== 'string' || !text.trim()) return true;
    const trimmed = text.trim();
    const lower = trimmed.toLowerCase();

    if (lower.includes('entityref=') || lower.includes('entitytype=')) return true;
    if (trimmed.startsWith('[{') || trimmed.includes('[{entityRef')) return true;
    if (/^Q\d+$/i.test(trimmed)) return true;
    if (lower.includes('unsupported mutation op')) return true;
    if (lower.includes('requires fromentityref') || lower.includes('requires toentityref')) return true;
    if (lower.includes('requires entityref and label')) return true;
    if (lower.includes('missing endpoint shape')) return true;
    if (lower.includes('not an entity card')) return true;
    if (lower.includes('→') && /Q\d+/i.test(trimmed) && lower.includes('missing')) return true;

    return false;
}

function extractHumanLabelFromTechnical(text) {
    if (typeof text !== 'string') return null;
    const labelMatch = text.match(/label=([^,}\]]+)/);
    return labelMatch ? labelMatch[1].trim() : null;
}

function sanitizeDisplayName(name) {
    if (!name || typeof name !== 'string') return 'That entity';
    const extracted = extractHumanLabelFromTechnical(name);
    if (extracted && !isTechnicalChartMessage(extracted)) return extracted;
    const trimmed = name.trim();
    if (!trimmed || isTechnicalChartMessage(trimmed)) return 'That entity';
    return trimmed;
}

function extractLabelFromCannotConnect(warning) {
    const match = warning.match(/^Cannot connect:\s*(.+?)\s+not on chart/i);
    if (!match) return null;
    return sanitizeDisplayName(match[1]);
}

/**
 * Convert a backend warning into a short user-facing note, or null to hide it.
 * @param {string} warning
 * @returns {string|null}
 */
export function formatChartWarningForUser(warning) {
    if (typeof warning !== 'string' || !warning.trim()) return null;
    const w = warning.trim();

    if (isInternalChartWarning(w)) return null;

    let match = w.match(/^Arrow (.+?)→(.+?)(?: \((.+?)\))? already on chart$/i);
    if (match) {
        return null;
    }

    match = w.match(/^The link between (.+?) and (.+?)(?: \((.+?)\))? is already on the chart\.$/i);
    if (match) {
        return null;
    }

    match = w.match(/^Cannot connect:\s*(.+?)\s+not on chart(?: \(and not planned to add\))?$/i);
    if (match) {
        const label = sanitizeDisplayName(match[1]);
        return `"${label}" is not on the chart yet, so that link was skipped.`;
    }

    match = w.match(/^"(.+?)" is not on the chart yet, so that link was skipped\.$/i);
    if (match) return w;

    match = w.match(/^(.+?) is not on the chart yet, so that link was skipped\.$/i);
    if (match) return w;

    match = w.match(/^(.+?) already on chart$/i);
    if (match) {
        return null;
    }

    match = w.match(/^(.+?) is already on the chart\.$/i);
    if (match) return null;

    if (isTechnicalChartMessage(w)) {
        if (/cannot connect/i.test(w)) {
            const label = extractLabelFromCannotConnect(w) || extractHumanLabelFromTechnical(w);
            if (label && label !== 'That entity') {
                return `"${label}" is not on the chart yet, so that link was skipped.`;
            }
            return 'Some links were skipped because the related entities are not on the chart yet.';
        }
        return null;
    }

    return w;
}

/**
 * Row/banner messages that should never appear inside chart mutation preview cards.
 * @param {string|Error|unknown} error
 * @returns {boolean}
 */
export function isHiddenChartMutationMessage(error) {
    const raw = typeof error === 'string' ? error : error?.message || String(error || '');
    if (!raw.trim()) {
        return false;
    }
    const lower = raw.toLowerCase();
    if (lower.includes('chart editor is not available') || lower.includes('open chart to apply')) {
        return true;
    }
    if (isInternalChartWarning(raw)) {
        return true;
    }
    if (/entity card missing on chart|connection missing on chart|cannot update|unsupported mutation/i.test(raw)) {
        return false;
    }
    return Boolean(formatChartWarningForUser(raw));
}

/**
 * Convert a mutation/apply error into a user-facing message.
 * @param {string|Error|unknown} error
 * @returns {string}
 */
export function formatChartErrorForUser(error) {
    const raw = typeof error === 'string' ? error : error?.message || String(error || '');
    if (!raw.trim()) return 'This change could not be applied.';

    const lower = raw.toLowerCase();
    if (lower.includes('chart editor is not available') || lower.includes('open chart to apply')) {
        return 'Open the chart to apply this change.';
    }
    if (/entity card missing on chart|connection missing on chart/i.test(raw)) {
        return 'This link could not be added because one of the entities is not on the chart yet.';
    }
    if (/cannot update/i.test(raw)) {
        return 'This update could not be applied because the chart item is missing.';
    }
    if (/unsupported mutation/i.test(raw)) {
        return 'This change type is not supported yet.';
    }

    const friendly = formatChartWarningForUser(raw);
    if (friendly) return friendly;

    if (isTechnicalChartMessage(raw) || isInternalChartWarning(raw)) {
        return 'This change could not be applied.';
    }

    return raw;
}

/**
 * True when an apply error means an arrow could not find its endpoint cards.
 * These are soft-failures: entities in the same batch should still apply.
 *
 * @param {string|null|undefined} message
 * @returns {boolean}
 */
export function isSoftArrowEndpointError(message) {
    if (typeof message !== 'string' || !message.trim()) return false;
    return /entity card missing on chart|connection missing on chart|not on the chart yet/i.test(message);
}

/**
 * User-facing apply summary for auto-apply / preview toasts.
 *
 * @param {{
 *   applied?: number,
 *   skipped?: number,
 *   failed?: Array<{op?: object, error?: string, soft?: boolean}>,
 *   warnings?: string[],
 * }} result
 * @returns {{ok: boolean, message: string|null, softOnly: boolean}}
 */
export function summarizeMarkupApplyResult(result) {
    const applied = result?.applied || 0;
    const skipped = result?.skipped || 0;
    const failed = Array.isArray(result?.failed) ? result.failed : [];
    const warnings = Array.isArray(result?.warnings) ? result.warnings : [];
    const results = Array.isArray(result?.results) ? result.results : [];
    const skippedRemovals = results.filter((entry) => entry?.skipped && (
        entry?.op?.type === 'remove-entity'
        || entry?.op?.type === 'remove-arrow'
    ));
    const softArrowFails = failed.filter((f) => f?.soft || isSoftArrowEndpointError(f?.error));
    const hardFails = failed.filter((f) => !(f?.soft || isSoftArrowEndpointError(f?.error)));
    const softOnly = softArrowFails.length > 0 && hardFails.length === 0;

    if (applied > 0 && softOnly) {
        const linkNote = softArrowFails.length === 1
            ? 'One link was skipped because an endpoint was not on the chart yet.'
            : 'Some links were skipped because endpoints were not on the chart yet.';
        return {
            ok: true,
            softOnly: true,
            message: `Added ${applied} chart change${applied === 1 ? '' : 's'}. ${linkNote}`,
        };
    }
    if (applied > 0 && hardFails.length > 0) {
        const detail = hardFails[0]?.error || 'Unknown error.';
        return {
            ok: false,
            softOnly: false,
            message: `Added ${applied} chart change${applied === 1 ? '' : 's'}, but some failed: ${detail}`,
        };
    }
    if (applied > 0) {
        return {
            ok: true,
            softOnly: false,
            message: `Applied ${applied} chart change${applied === 1 ? '' : 's'}.`,
        };
    }
    if (skippedRemovals.length > 0 && applied === 0) {
        const label = skippedRemovals[0]?.op?.label || skippedRemovals[0]?.op?.ref || 'entity';
        return {
            ok: false,
            softOnly: false,
            message: `Could not apply chart changes: ${label} was not found on the chart.`,
        };
    }
    if (skipped > 0 && failed.length === 0 && warnings.length === 0 && applied === 0) {
        return {ok: true, softOnly: false, message: 'Chart already up to date.'};
    }
    if (softOnly) {
        return {
            ok: false,
            softOnly: true,
            message: softArrowFails[0]?.error
                || 'Links could not be added because entities are not on the chart yet.',
        };
    }
    if (failed.length > 0 || warnings.length > 0) {
        const detail = hardFails[0]?.error || failed[0]?.error || warnings[0]
            || 'No chart changes were applied.';
        return {
            ok: false,
            softOnly: false,
            message: `Could not apply chart changes: ${detail}`,
        };
    }
    return {
        ok: false,
        softOnly: false,
        message: 'Could not apply chart changes: No chart changes were applied.',
    };
}
